import classnames from 'classnames';
import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import BubbleContent from 'components/customer/conversation_history/conversation_items_v2/content/bubble_content';
import ItemMetadata from 'components/customer/conversation_history/conversation_items_v2/item_metadata';
import VoiceAIPhoneMetadataIcon from 'components/lib/icons/voiceai_phone_metadata_icon';
import Row from 'components/common/grid/row';
import ConversationItem from 'models/conversation_item';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import CollapseVoiceAICallTranscript from 'scripts/application/actions/voiceai/collapse_voiceai_call_transcript';
import { formatPhoneNumber } from 'models/phone_number';
import ItemContent from '../item_content';
import VoiceAIRecordingWavePlayerContainer from '../phone/recordings/voiceai_recording_wave_player_container';
import RecordingStatus from 'models/recording_status';
import Timer from 'components/lib/date/timer';
import VoiceAIMessageItem from './voiceai_message_item';

export default function VoiceAICallItem({ className, item }) {
  const tooltip = `called ${formatPhoneNumber(item.content.companyNumber)} from ${formatPhoneNumber(
    item.content.customerNumber
  )}`;
  return (
    <>
      <ItemContent item={item}>
        <ItemMetadata icon={<VoiceAIPhoneMetadataIcon />} item={item} object={' Gladly AI'} tooltip={tooltip} />
        {item.content.recordings && item.content.recordings.length > 0 ? (
          <VoiceAIRecordingItemBubble item={item} />
        ) : (
          <VoiceAICallItemWithoutRecordingBubble className={className} item={item} />
        )}
      </ItemContent>

      <Transcript item={item} />
    </>
  );
}

export function Transcript({ item }) {
  const executeAction = useExecuteAction();
  const [isTranscriptCollapsed, setCollapsed] = useState(item.content.isTranscriptCollapsed);
  const collapseOrExpandTranscript = () => {
    const newValue = !isTranscriptCollapsed;

    executeAction(CollapseVoiceAICallTranscript, { voiceAICallItemId: item.id, isTranscriptCollapsed: newValue });
    setCollapsed(newValue);
  };

  const transcript = [];
  const messages = item.content.getTranscribedMessages();
  for (let i = 0; i < messages.length; i++) {
    const isFirstInStack = i === 0 || (i > 0 && messages[i - 1].initiator.type !== messages[i].initiator.type);
    transcript.push(
      <VoiceAIMessageItem
        isFirstInStack={isFirstInStack}
        isStacked={!isFirstInStack}
        item={messages[i]}
        key={messages[i].id}
      />
    );
  }

  return (
    <>
      <StyledRow>
        <StyledText>{isTranscriptCollapsed ? 'Show transcript' : 'Hide transcript'}</StyledText>
        <StyledActivityExpander onClick={collapseOrExpandTranscript}>
          <StyledChevron $isDownwards={isTranscriptCollapsed} className="ion-chevron-up" />
        </StyledActivityExpander>
      </StyledRow>
      {!isTranscriptCollapsed &&
        (transcript.length > 0 ? (
          transcript
        ) : (
          <StyledEmptyTranscriptText>No speech detected</StyledEmptyTranscriptText>
        ))}
    </>
  );
}

export function VoiceAICallItemWithoutRecordingBubble({ className, item }) {
  const staticWave = (
    <div className="voiceItem-callWave">
      <div className="voiceItem-waveForm" />
      <div className="voiceItem-duration">{Timer.displayDuration(item.content.callDuration() * 1000 || 0)}</div>
    </div>
  );

  const dynamicWave = (
    <div className="voiceItem-callWave">
      <div className={classnames('voiceItem-waveForm', 'voiceItem-waveForm-playAnimation')}>
        <div className="voiceItem-waveForm-progress">
          <div className={classnames('voiceItem-waveForm-animationBlocker')} />
        </div>
      </div>
      <Timer className="voiceItem-timer" startTime={item.content.startedAt()} />
    </div>
  );

  return (
    <BubbleContent className={classnames('voiceAIRecordingItem-content', className)} item={item}>
      {item.content.isOngoingCall() ? dynamicWave : staticWave}
    </BubbleContent>
  );
}

export function VoiceAIRecordingItemBubble({ className, item }) {
  const recordingItem = item.content.recordings[0]; // only automated recording for now
  const voiceAIRecording = recordingItem.content;

  let content = (
    <div className="voiceItem-callWave">
      <div className="voiceItem-waveForm" />
      <div className="voiceItem-duration">{getCallDuration()}</div>
    </div>
  );
  if (voiceAIRecording.recordingStatus === RecordingStatus.AVAILABLE) {
    content = <VoiceAIRecordingWavePlayerContainer item={recordingItem} />;
  }

  if (voiceAIRecording.recordingStatus === RecordingStatus.DELETED) {
    content = <div className="voiceItem-recordingDeleted">Recording has been deleted</div>;
  }

  if (voiceAIRecording.recordingStatus === RecordingStatus.ERROR) {
    content = <div className="voiceItem-recordingError">Recording not available due to error</div>;
  }

  let contentClasses = classnames(
    'voiceAIRecordingItem-content',
    {
      'voiceAIRecordingItem-content-recordingDeleted': voiceAIRecording.recordingStatus === RecordingStatus.DELETED,
    },
    className
  );
  return (
    <BubbleContent className={contentClasses} data-aid={`voiceAIRecordingItem-${item.id}`} item={item}>
      {content}
    </BubbleContent>
  );

  function getCallDuration() {
    return Timer.displayDuration(voiceAIRecording.recordingDuration * 1000 || 0);
  }
}

VoiceAIRecordingItemBubble.propTypes = {
  className: PropTypes.string,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

VoiceAICallItem.propTypes = {
  className: PropTypes.string,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  voiceAIRecordingItems: PropTypes.arrayOf(PropTypes.instanceOf(ConversationItem)),
};

const StyledActivityExpander = styled.div`
  height: 18px;
  width: 18px;
  color: ${p => p.theme.colors.gray700};
  cursor: pointer;
  display: flex;
  justify-content: center;
  &:hover {
    background: var(--color-gray-xxxxlight);
    color: ${p => p.theme.colors.green400};
  }
`;

const StyledChevron = styled.div`
  cursor: pointer;
  transition: transform 0.15s ease;
  ${p => (p.$isDownwards ? `transform: rotate(180deg) translateY(-2px);` : `transform: translateY(2px);`)};
`;

const StyledText = styled.div`
  display: flex;
  padding-left: 12px;
  color: ${p => p.theme.colors.gray700};
  font-size: var(--font-size-reduced);
`;

const StyledRow = styled(Row)`
  padding-left: 60px;
  padding-bottom: 8px;
`;

const StyledEmptyTranscriptText = styled(StyledText)`
  padding-left: 70px;
  font-style: italic;
  font-weight: normal;
`;
